// Pantry-tab tile taxonomy + classifier.
//
// Mirror of fridgeTiles.js for items whose physical location is "pantry".
// Eleven tiles covering the shape of a well-stocked home pantry, plus a
// bonus "Dried Chilies" tile for anyone doing Mexican cooking seriously.
//
// Same contract as fridgeTiles: PANTRY_TILES (display order) and
// pantryTileIdForItem(item, { findIngredient, hubForIngredient }) which
// returns a tile id — never null — so every pantry row lands somewhere
// reachable via drill-down.

import { resolveStorageFromHubChain } from "../data/ingredients";

export const PANTRY_TILES = [
  { id: "pasta_grains",      emoji: "🍝", label: "Pasta & Grains",      blurb: "Dried pasta, rice, farro, quinoa, oats, polenta" },
  { id: "beans_legumes",     emoji: "🫘", label: "Beans & Legumes",     blurb: "Canned beans, dried beans, lentils, chickpeas" },
  { id: "canned_jarred",     emoji: "🥫", label: "Canned & Jarred",     blurb: "San Marzano tomatoes, stock, tuna, coconut milk" },
  { id: "baking",            emoji: "🌾", label: "Baking",              blurb: "Flour, sugar, baking soda, cocoa, vanilla" },
  { id: "spices_dried_herbs",emoji: "🧂", label: "Spices & Dried Herbs",blurb: "Salt, pepper, cumin, paprika, bay leaves" },
  { id: "condiments_sauces", emoji: "🫙", label: "Condiments & Sauces", blurb: "Soy sauce, fish sauce, mustard, vinegars" },
  { id: "oils_fats",         emoji: "🧈", label: "Oils & Fats",         blurb: "Olive oil, neutral oil, sesame oil, ghee" },
  { id: "sweeteners",        emoji: "🍯", label: "Sweeteners",          blurb: "Honey, maple syrup, molasses, agave" },
  { id: "nuts_seeds",        emoji: "🥜", label: "Nuts & Seeds",        blurb: "Almonds, pine nuts, sesame, nut butters" },
  { id: "cooking_alcohol",   emoji: "🍷", label: "Cooking Alcohol",     blurb: "White wine, red wine, sake, vermouth" },
  { id: "bread",             emoji: "🍞", label: "Bread",               blurb: "Tortillas, pita, naan, sandwich bread, bagels" },
  { id: "dried_chilies",     emoji: "🌶️", label: "Dried Chilies",      blurb: "Ancho, guajillo, pasilla, chipotle, árbol" },
  { id: "jerky_snacks",      emoji: "🥩", label: "Jerky & Snack Meats", blurb: "Beef sticks, jerky, biltong, dried sausage, pepperoni sticks" },
  { id: "dried_fruit",       emoji: "🍇", label: "Dried Fruit & Trail Mix", blurb: "Raisins, dates, apricots, cranberries, mango, trail mix" },
  { id: "shelf_stable_dairy",emoji: "🥛", label: "Shelf-Stable Dairy",    blurb: "UHT milk, evaporated, sweetened condensed, powdered milk" },
  { id: "pickles_ferments",  emoji: "🥒", label: "Pickles & Ferments",    blurb: "Pickles, sauerkraut, kimchi, olives, capers, hot ferments" },
  { id: "candy_chocolate",   emoji: "🍬", label: "Candy & Chocolate",     blurb: "Candy bars, chocolate, peanut butter cups, gum, mints" },
  // Cookies, crackers, chips — the eaten-as-snack baked goods. Distinct
  // from candy_chocolate (sweet confections) and from baking (raw
  // baking ingredients like flour and chocolate chips). Without this
  // tile, "Goldfish" and "Oreo" landed in misc.
  { id: "snacks",            emoji: "🍪", label: "Cookies & Snacks",      blurb: "Cookies, crackers, chips, pretzels, popcorn" },
  // Pantry drinks — coffee, tea, soda, sealed juice, water. Most users
  // store these on the pantry shelf or counter, not in the fridge, so
  // they need their own tile distinct from the fridge's drinks tile.
  // Id is `pantry_drinks` (NOT `drinks`) to avoid colliding with the
  // fridge's `drinks` tile — `tileMetaById` walks PANTRY → FRIDGE →
  // FREEZER and would otherwise return the pantry label for a fridge
  // row's tile.
  { id: "pantry_drinks",     emoji: "🥤", label: "Drinks",                blurb: "Coffee, tea, soda, water, sealed juice" },
  // Counter produce — onions, garlic, tomato, shallots, avocado: produce
  // that explicitly does NOT belong in the fridge. The INGREDIENT_INFO
  // for these declares storage.location: "pantry"; without a tile they
  // land in misc. Everyone has this drawer or basket. Id is
  // `counter_produce` (NOT `produce`) to avoid colliding with the
  // fridge's `produce` tile — same collision rationale as drinks above.
  { id: "counter_produce",   emoji: "🧄", label: "Counter Produce",       blurb: "Onions, garlic, tomatoes on the counter, potatoes, shallots" },
  // Catch-all — anything the classifier couldn't place. Always
  // reachable so items don't vanish when moved here from another
  // location with a different tile taxonomy.
  { id: "misc",              emoji: "📦", label: "Miscellaneous",       blurb: "Anything that doesn't fit the other tiles" },
];

// Most of the granular tiles don't have registry ingredients yet — the
// sets are forward-looking so when the registry expands (baking aisle,
// spice rack, the dried chili pantry) the classifier routes correctly
// without another edit. Same pattern as fridgeTiles.

const PASTA_GRAIN_IDS = new Set([
  "spaghetti", "linguine", "bucatini", "penne", "rigatoni", "fettuccine",
  "pappardelle", "tagliatelle", "lasagna_sheets", "conchiglie",
  "orzo", "couscous", "rice", "jasmine_rice", "basmati_rice", "arborio",
  "brown_rice", "wild_rice", "farro", "barley", "quinoa", "polenta",
  "cornmeal", "oats", "rolled_oats", "steel_cut_oats",
  // Asian noodles + ancient grains — same shelf, same tile.
  "ramen", "udon", "soba",
  "ramen_noodles", "udon_noodles", "soba_noodles", "egg_noodles",
  "bulgur", "millet",
  // Boxed pasta meals — their primary ingredient is the pasta.
  "mac_and_cheese",
]);

const BEAN_LEGUME_IDS = new Set([
  "black_beans", "kidney_beans", "pinto_beans", "cannellini", "navy_beans",
  "garbanzo", "chickpeas", "lentils", "red_lentils", "green_lentils",
  "split_peas", "black_eyed_peas", "lima_beans", "butter_beans",
  "white_beans", "fava_beans", "mung_beans", "french_lentils",
]);

// Canned & jarred — shelf-stable preserved items. Stock is here (vs.
// Condiments) because culturally it sits with canned tomatoes and tuna
// on the pantry shelf.
const CANNED_JARRED_IDS = new Set([
  "canned_tomatoes", "crushed_tomatoes", "diced_tomatoes", "whole_tomatoes",
  "tomato_paste", "tomato_sauce", "passata",
  "coconut_milk", "coconut_cream",
  "chicken_stock", "beef_stock", "vegetable_stock", "bone_broth",
  "fish_stock", "dashi",
  "canned_tuna", "tuna", "sardines", "anchovies",
  "artichokes", "roasted_peppers", "capers", "olives",
  "chipotles_adobo", "salsa_verde",
  "sun_dried_tomatoes",
]);

const BAKING_IDS = new Set([
  "flour", "bread_flour", "cake_flour", "pastry_flour", "whole_wheat_flour",
  "tapioca_flour", "arrowroot", "rye_flour", "buckwheat_flour",
  "sugar", "brown_sugar", "powdered_sugar", "turbinado", "demerara",
  "white_sugar", "turbinado_sugar",
  "baking_soda", "baking_powder", "yeast", "cornstarch",
  "active_dry_yeast", "instant_yeast",
  "cocoa", "cocoa_powder", "chocolate_chips", "dark_chocolate",
  "vanilla", "vanilla_extract", "almond_extract",
  // Dry-crumb / cracker family lives with baking — they share a shelf
  // with flour and cornstarch in most kitchens and they ARE derivatives
  // of stale bread. Moved out of the old bread_dry_goods tile so Bread
  // can be a proper "actual bread" tile (tortillas, pita, sandwich bread).
  "crackers", "saltines", "breadcrumbs", "panko", "croutons",
  "melba_toast", "rice_cakes", "water_crackers",
]);

// Dried spices — bay leaves and dried herbs live here, not in Fresh Herbs
// (which is a fridge-only tile). Dried sea greens / seaweed land here
// too — they're the savory equivalent of dried herbs.
const SPICE_DRIED_HERB_IDS = new Set([
  // Salts
  "salt", "kosher_salt", "sea_salt", "table_salt", "flaky_salt",
  "smoked_salt", "truffle_salt", "celery_salt", "garlic_salt",
  "onion_salt", "seasoned_salt", "msg",
  // Peppers & heat
  "black_pepper", "white_pepper", "peppercorns", "lemon_pepper",
  "red_pepper_flakes", "cayenne", "chili_powder",
  // Core ground spices
  "cumin", "ground_cumin", "cumin_seed",
  "paprika", "smoked_paprika", "sweet_paprika",
  "cinnamon", "ground_cinnamon",
  "turmeric", "coriander", "ground_coriander", "cardamom",
  "cloves", "nutmeg", "allspice", "star_anise", "mace",
  "fennel_seed", "mustard_seed", "ground_mustard",
  "cream_of_tartar",
  "saffron", "annatto",
  // Dried herbs
  "oregano", "dried_oregano", "bay_leaves",
  "dried_thyme", "dried_rosemary", "dried_sage",
  "dried_basil", "dried_parsley", "dried_dill",
  "dried_tarragon", "dried_marjoram",
  "dried_chives", "dried_mint",
  // Powders
  "garlic_powder", "onion_powder", "ginger_powder",
  "gochugaru",  // Korean chili flakes
  // Seeds
  "poppy_seed", "caraway_seed", "celery_seed",
  "white_sesame", "black_sesame",
  "fenugreek", "juniper_berries",
  // Dried sea greens — savory pantry shelf-staples in Asian cooking.
  "nori", "wakame", "kombu", "hijiki", "dashi_powder",
  // Blends
  "curry_powder", "garam_masala",
  "old_bay", "zaatar", "sumac",
  "five_spice", "ras_el_hanout", "berbere", "dukkah",
  "italian_seasoning", "herbs_de_provence",
  "taco_seasoning", "ranch_seasoning", "everything_bagel",
  "cajun_seasoning", "jerk_seasoning",
  "furikake", "togarashi",
]);

const CONDIMENT_SAUCE_IDS = new Set([
  "soy_sauce", "tamari", "fish_sauce", "mirin",
  "miso", "white_miso", "red_miso",
  "hot_sauce", "sriracha", "tabasco", "gochujang", "harissa",
  "mustard", "dijon", "yellow_mustard", "whole_grain_mustard", "dijon_mustard",
  "ketchup", "worcestershire", "tahini", "hoisin", "hoisin_sauce", "oyster_sauce",
  "mayo", "mayonnaise",
  "vinegar", "rice_vinegar", "rice_wine_vinegar",
  "white_vinegar", "apple_cider_vinegar",
  "red_wine_vinegar", "white_wine_vinegar", "champagne_vinegar",
  "sherry_vinegar", "balsamic", "balsamic_vinegar", "malt_vinegar",
  "pesto",
  // Sweet condiments + spreads
  "jam", "jelly", "marmalade", "preserves",
  "chocolate_spread", "hazelnut_spread", "hot_honey",
  // BBQ / wing sauces
  "buffalo_sauce", "wing_sauce", "bbq_sauce",
  // Pickle relish + dressings (shelf-stable bottle types)
  "relish",
  "ranch_dressing", "blue_cheese_dressing", "italian_dressing",
  "vinaigrette",
  // Korean fermented sauce-pastes (shelf-stable jars)
  "ponzu", "doenjang", "ssamjang",
  // Curry pastes — Thai cooking pantry staples
  "curry_paste_red", "curry_paste_green",
  "curry_paste_yellow", "curry_paste_massaman",
  // Floral waters + Mediterranean condiments
  "pomegranate_molasses", "rose_water", "orange_blossom_water",
  // Horseradish (sometimes refrigerated; the jar-sealed lives in pantry)
  "horseradish",
]);

const OIL_FAT_IDS = new Set([
  "olive_oil", "extra_virgin_olive_oil",
  "vegetable_oil", "canola_oil", "neutral_oil", "grapeseed_oil",
  "sunflower_oil", "safflower_oil", "avocado_oil",
  "sesame_oil", "toasted_sesame_oil",
  "peanut_oil",
  "coconut_oil", "ghee", "lard", "duck_fat", "schmaltz",
  "shortening",
]);

const SWEETENER_IDS = new Set([
  "honey", "maple_syrup", "molasses", "agave", "agave_nectar", "piloncillo",
  "corn_syrup", "golden_syrup", "date_syrup",
]);

// Nut butters included here — peanut butter, almond butter, etc. —
// because when we think "where's the nut butter," we reach for the same
// shelf as the actual nuts.
const NUT_SEED_IDS = new Set([
  "almonds", "walnuts", "pecans", "cashews", "peanuts", "pistachios",
  "hazelnuts", "macadamia", "macadamia_nuts", "pine_nuts", "brazil_nuts",
  "sesame", "sesame_seeds", "pumpkin_seeds", "pepitas",
  "sunflower_seeds", "chia", "chia_seeds", "flax", "flax_seeds",
  "hemp_seeds", "poppy_seeds",
  "peanut_butter", "almond_butter", "cashew_butter", "nut_butter",
]);

const COOKING_ALCOHOL_IDS = new Set([
  "white_wine", "red_wine", "sake", "mirin",
  "vermouth", "dry_vermouth", "sweet_vermouth",
  "beer", "brandy", "cognac", "rum", "sherry", "port", "marsala",
]);

// Candy & chocolate — eaten-as-snack confections, distinct from
// SWEETENER_IDS (cooking-grade honey/syrups) and BAKING_IDS (cocoa
// powder, chocolate chips used as ingredients). The brand-classification
// seed batch added these canonicals; without this set they all fell
// through pantryTileIdForItem to the misc catch-all.
const CANDY_CHOCOLATE_IDS = new Set([
  "candy", "candy_bar",
  "chocolate", "chocolate_candy",
  "peanut_butter_candy", "peanut_butter_cup",
  "gum", "mints", "lollipop", "caramels",
  "gummy_bears", "gummies", "licorice",
  "marshmallows", "taffy", "fudge",
]);

// Bread — actual bread products people store on the counter or in the
// bread drawer. Tortillas, pita, and naan were falling through to the
// canned_jarred catch-all; they live here now. Dry cracker-family items
// (breadcrumbs, panko, croutons, saltines) moved into the Baking tile
// where they share a shelf with flour in most kitchens.
const BREAD_IDS = new Set([
  "tortillas", "corn_tortillas", "flour_tortillas",
  "flour_tortilla", "corn_tortilla",
  "pita", "pita_bread", "naan", "lavash", "flatbread", "roti",
  "sandwich_bread", "white_bread", "wheat_bread", "sourdough", "sourdough_bread",
  "baguette", "ciabatta", "focaccia", "rye_bread", "brioche",
  "bagels", "english_muffins", "hamburger_buns", "hot_dog_buns",
  "dinner_rolls", "kaiser_rolls", "challah",
  // Pastry doughs (often refrigerated/frozen but the pantry-shelf
  // shelf-stable form exists too — phyllo, puff_pastry sealed).
  "phyllo_dough", "puff_pastry", "pie_crust", "pizza_dough",
]);

// Dried chilies — its own tile per the user's spec. Only populated if
// someone's actually cooking mole or pozole.
const DRIED_CHILI_IDS = new Set([
  "ancho", "ancho_chili", "guajillo", "pasilla",
  "chipotle", "chipotle_morita", "arbol", "de_arbol",
  "cascabel", "mulato", "negro_chili", "new_mexico_chili",
]);

// Cookies, crackers, chips, pretzels — eaten-as-snack baked goods.
// Goldfish, Oreo, saltines (sleeve-form vs the cracker-as-baking
// ingredient — that distinction is per-canonical not per-id), Ritz.
// Cereal sits here too — it's the breakfast-aisle equivalent of
// these packaged carbs, and we don't have a separate breakfast tile.
// Distinct from candy_chocolate (sweet confections only).
const SNACK_BAKED_IDS = new Set([
  "cookie", "chocolate_chip_cookie", "sandwich_cookie", "cookies_and_cream",
  "cracker", "goldfish", "pretzel", "pretzels", "chip", "chips",
  "potato_chip", "potato_chips", "tortilla_chip", "tortilla_chips",
  "popcorn",
  "cereal", "lunchables",
]);

// Pantry drinks — coffee, tea, soda, sealed juice, water. Pantry-stable
// before opening; once opened, juice / soda often migrate to the fridge
// (user-level preference, captured by the placement override).
const PANTRY_DRINK_IDS = new Set([
  "coffee", "coffee_grounds", "instant_coffee", "espresso_grounds",
  "tea", "tea_bags", "loose_leaf_tea",
  "soda", "soft_drink", "cola", "ginger_ale", "root_beer",
  "water", "sparkling_water", "seltzer", "tonic_water",
  "coconut_water",
  "orange_juice", "apple_juice", "grape_juice",
  "lemon_juice", "lime_juice",
]);

// Counter produce — onions, garlic, tomatoes, shallots: produce that
// belongs OUT of the fridge per culinary tradition. INGREDIENT_INFO
// for these declares storage.location: "pantry"; without this set
// they'd land in misc.
const COUNTER_PRODUCE_IDS = new Set([
  "garlic", "garlic_clove",
  "yellow_onion", "white_onion", "red_onion", "shallot", "pearl_onion", "scallion",
  "tomato", "cherry_tomato", "grape_tomato", "roma_tomato", "heirloom_tomato",
  "potato", "russet_potato", "yukon_gold", "fingerling_potato",
  "sweet_potato", "yam",
  "avocado",
  // Whole basil (the bunch on the counter) before chopping; if it's in
  // the fridge it routes via fridgeTiles HERB_IDS to fresh_herbs.
  "basil",
  // Bananas / citrus / hard-fruit families that live on the counter
  // until ripe. Fridge-overrides land via per-row tile_id.
  "banana", "lemon", "lime", "apple", "pear", "orange",
]);

// Dried fruit & trail mix — date, raisin, prune, cranberry, mango, etc.
// Same shelf in most kitchens; bagged or jarred dried fruit sits with
// nuts and seeds visually but is its own tile per the user's spec.
const DRIED_FRUIT_IDS = new Set([
  "raisin", "raisins", "golden_raisins",
  "date", "dates", "medjool_date",
  "prune", "prunes",
  "dried_cranberry", "dried_cranberries", "craisins",
  "dried_apricot", "dried_apricots",
  "dried_fig", "dried_figs",
  "dried_blueberry", "dried_blueberries",
  "dried_mango", "dried_pineapple", "dried_papaya",
  "trail_mix", "granola",
]);

// Pickles & ferments — pickled cucumbers, sauerkraut, kimchi, olives,
// capers, preserved lemons. Shelf-stable in sealed jars; once opened
// most go to the fridge (per-row override via tile_id).
const PICKLES_FERMENTS_IDS = new Set([
  "pickle", "pickles", "dill_pickle", "bread_butter_pickle",
  "sauerkraut", "kimchi",
  "olives_kalamata", "olives_green", "olives_black",
  "kalamata_olives", "green_olives", "black_olives", "castelvetrano",
  "capers",
  "giardiniera",
  "preserved_lemon",
]);

// Ordered classifier. First match wins — order matters when an id could
// fit multiple tiles. Order here mirrors the PANTRY_TILES display order
// with one override: dried chilies are checked before spices so a
// chipotle doesn't get routed to the generic spice tile.
export function pantryTileIdForItem(item, helpers) {
  const { findIngredient, hubForIngredient, getInfo } = helpers || {};
  // Explicit user placement wins (migration 0036) — see fridgeTiles.js
  // for the rationale. User-set tile_id trumps the heuristic.
  if (item?.tileId) return item.tileId;
  if (item?.pantryTile) return item.pantryTile;

  // Haiku-suggested override — info.tile_routing.pantry. Reads via
  // the optional getInfo helper; bare classifier calls without it
  // fall through to the heuristic stack below.
  if (item?.ingredientId && typeof getInfo === "function") {
    const info = getInfo(item.ingredientId);
    const tileFromInfo = info?.tile_routing?.pantry;
    if (typeof tileFromInfo === "string" && tileFromInfo.trim()) {
      return tileFromInfo.trim();
    }
  }

  // State-axis preservation states win before canonical lookups.
  // Routing splits by the canonical's underlying category so the
  // same state ("dried", "cured") can land in different tiles based
  // on what's BEEN dried/cured. Pre-resolve the canonical once for
  // category-aware routing below.
  const stateIng = item?.canonicalId
    ? (findIngredient(item.canonicalId) || null)
    : (item?.ingredientId ? (findIngredient(item.ingredientId) || null) : null);
  const stateCat = stateIng?.category || null;

  if (item?.state === "jerky" || item?.state === "cured") {
    // Always meat-context — jerky / cured aren't ambiguous across
    // food families. Slim Jim, biltong, salami sticks, etc.
    return "jerky_snacks";
  }
  if (item?.state === "dried") {
    // Dried meat → jerky_snacks; dried produce / unknown → dried_fruit.
    // Default skews to dried_fruit because the meat-preserved forms
    // typically trigger their own more-specific state ("jerky" /
    // "cured") via the inference patterns — anything that lands as
    // generic "dried" is almost always raisins / apricots / dates /
    // sun-dried tomatoes / dried mushrooms.
    if (stateCat === "meat" || stateCat === "seafood") {
      return "jerky_snacks";
    }
    return "dried_fruit";
  }
  if (item?.state === "canned") {
    // Canned meats / fish (SPAM, canned tuna / salmon / chicken,
    // sardines) AND canned beans / tomatoes / soups all live in
    // canned_jarred. Same shelf-stable logic applies regardless of
    // canonical category: a row with state=canned has no business
    // outside the canned aisle.
    return "canned_jarred";
  }
  if (item?.state === "uht" || item?.state === "evaporated" ||
      item?.state === "condensed" || item?.state === "powdered") {
    // Shelf-stable dairy in any preservation form. UHT milk in a
    // brik, evaporated / sweetened condensed cans, powdered milk
    // tins — all room-temp until opened. Separate tile from
    // canned_jarred so users find "milk in a tetra pak" by looking
    // for milk, not by looking for canned goods.
    return "shelf_stable_dairy";
  }
  if (item?.state === "pickled" || item?.state === "fermented") {
    // Pickles, sauerkraut, kimchi, olives, capers — sealed jars
    // are pantry-stable for years. The opened-flips-to-fridge
    // transition is a separate flow (handled in the location effect
    // when remaining < 1 ⇒ user manually re-locates).
    return "pickles_ferments";
  }

  const ing = item?.ingredientId ? findIngredient(item.ingredientId) : null;

  // Dried chilies first — more specific than the general spice tile.
  if (ing && DRIED_CHILI_IDS.has(ing.id)) return "dried_chilies";

  // Declarative hub-chain home — `hub.storage.tile.pantry` lives on
  // each hub in src/data/ingredients.js. Replaces the old set of
  // hub-id-hardcoded checks (rice_hub, pasta_hub, bean_hub, bread_hub
  // etc) so adding a new hub doesn't require a classifier edit.
  // resolveStorageFromHubChain walks parentHub too, so sub-hubs
  // (pepper_hub → vegetable_hub) inherit without re-declaring.
  if (ing) {
    const fromChain = resolveStorageFromHubChain(ing);
    const tileFromHub = fromChain?.tile;
    if (typeof tileFromHub === "string" && fromChain?.location === "pantry") {
      return tileFromHub;
    }
    if (tileFromHub && typeof tileFromHub === "object" && typeof tileFromHub.pantry === "string") {
      return tileFromHub.pantry;
    }
  }

  if (ing && PASTA_GRAIN_IDS.has(ing.id))      return "pasta_grains";
  if (ing && BEAN_LEGUME_IDS.has(ing.id))      return "beans_legumes";
  // Baking before canned_jarred so breadcrumbs / panko / crackers route
  // to their new home instead of hitting the free-text fallback below.
  // Candy & snack-baked checked BEFORE BAKING_IDS so a Hershey's bar
  // or a sleeve of Oreos routes to candy_chocolate / snacks instead
  // of baking (BAKING_IDS contains cocoa / chocolate_chips, which DO
  // belong with flour and sugar because they're recipe ingredients,
  // not eaten-as-snack confections).
  if (ing && CANDY_CHOCOLATE_IDS.has(ing.id))  return "candy_chocolate";
  if (ing && SNACK_BAKED_IDS.has(ing.id))      return "snacks";
  if (ing && DRIED_FRUIT_IDS.has(ing.id))      return "dried_fruit";
  if (ing && PICKLES_FERMENTS_IDS.has(ing.id)) return "pickles_ferments";
  if (ing && BAKING_IDS.has(ing.id))           return "baking";
  if (ing && BREAD_IDS.has(ing.id))            return "bread";
  if (ing && CANNED_JARRED_IDS.has(ing.id))    return "canned_jarred";
  if (ing && SPICE_DRIED_HERB_IDS.has(ing.id)) return "spices_dried_herbs";
  if (ing && CONDIMENT_SAUCE_IDS.has(ing.id))  return "condiments_sauces";
  if (ing && OIL_FAT_IDS.has(ing.id))          return "oils_fats";
  if (ing && SWEETENER_IDS.has(ing.id))        return "sweeteners";
  if (ing && NUT_SEED_IDS.has(ing.id))         return "nuts_seeds";
  if (ing && COOKING_ALCOHOL_IDS.has(ing.id))  return "cooking_alcohol";
  if (ing && PANTRY_DRINK_IDS.has(ing.id))     return "pantry_drinks";
  if (ing && COUNTER_PRODUCE_IDS.has(ing.id))  return "counter_produce";

  // Free-text fallback: the item has no canonical ingredient match. Before
  // dumping into canned_jarred, do a keyword scan on the item's name so
  // common un-linked entries route to the right tile instead of the catch-all.
  // This catches "Italian Seasoning", "Garlic Powder", "Ground Cumin", etc.
  // that users added manually without picking a registry ingredient.
  const lower = (item?.name || "").toLowerCase();
  const spiceKeywords = [
    "powder", "ground", "dried", "seasoning", "spice", "herb",
    "pepper", "salt", "cumin", "paprika", "oregano", "thyme",
    "rosemary", "cinnamon", "turmeric", "nutmeg", "allspice",
    "cayenne", "chili", "garlic powder", "onion powder",
    "garam", "curry", "saffron", "zaatar", "sumac", "flakes",
  ];
  if (spiceKeywords.some(kw => lower.includes(kw))) return "spices_dried_herbs";

  // Miscellaneous is the residual catch-all — always reachable via
  // drill-down so nothing silently drops out of the UI. Canned &
  // Jarred is reserved for items that actually match the canned
  // keywords / ids above, not a de-facto dumping ground.
  return "misc";
}
